import { cn } from 'lib/utils'
import { DateTimePicker } from 'primitives/date-time-picker'
import { Select, type SelectOption } from 'primitives/select'
import type { TimePickerFormat } from 'primitives/time-picker/time-picker-utils'
import { Tooltip } from 'primitives/tooltip'
import { useEffect, useRef, useState } from 'react'
import ContentEditable from 'react-contenteditable'
import type { Matcher } from 'react-day-picker'

interface InlineEditProps {
  name: string
  initialHtml?: string
  disabled?: boolean
  tagName?: string
  onChange?: (field_name: string, value: string) => void
  onBlur?: (field_name: string, value: string) => void
  onEnter?: (field_name: string, value: string) => void
  className?: string
  disabledReason?: string
  focusOnMount?: boolean
  maxLength?: number
  'data-testid'?: string
}

const InlineEdit = ({
  name,
  initialHtml = '',
  disabled = false,
  tagName = 'div',
  onChange,
  onBlur,
  onEnter,
  className = '',
  disabledReason,
  focusOnMount = true,
  maxLength,
  'data-testid': dataTestId,
}: InlineEditProps) => {
  const [html, setHtml] = useState(initialHtml)
  const contentEditableRef = useRef<HTMLElement>(null)

  const handleChange = (evt: { currentTarget: { textContent: string | null } }) => {
    let text = evt.currentTarget.textContent ?? ''

    // Truncate to maxLength — handles both typing and paste
    if (maxLength && text.length > maxLength) {
      text = text.slice(0, maxLength)
      if (contentEditableRef.current) {
        contentEditableRef.current.textContent = text
      }
    }

    setHtml(text)
    onChange?.(name, text)
  }

  const handleKeyDown = (evt: React.KeyboardEvent) => {
    if (evt.key === 'Enter' && onEnter) {
      evt.preventDefault()
      const currentValue = contentEditableRef.current?.textContent || ''
      onEnter(name, currentValue)
      return
    }
    if (evt.key === 'Escape') {
      evt.preventDefault()
      const node = contentEditableRef.current
      // Revert the DOM synchronously so the blur below commits the reverted value (a no-op),
      // and notify consumers so onChange- and onBlur-driven state both revert in lockstep.
      if (node) node.textContent = initialHtml
      setHtml(initialHtml)
      onChange?.(name, initialHtml)
      node?.blur()
    }
  }

  useEffect(() => {
    if (contentEditableRef.current && !disabled && focusOnMount) {
      contentEditableRef.current.focus()
      const range = document.createRange()
      const selection = window.getSelection()
      const element = contentEditableRef.current
      range.selectNodeContents(element)
      range.collapse(false)
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
  }, [disabled])

  useEffect(() => {
    const element = contentEditableRef.current
    const handleBlur = () => {
      // Read the live DOM, same source as the Enter path — avoids re-subscribing per keystroke.
      onBlur?.(name, element?.textContent ?? '')
    }
    element?.addEventListener('blur', handleBlur)
    return () => {
      element?.removeEventListener('blur', handleBlur)
    }
  }, [name, onBlur])

  useEffect(() => {
    setHtml(initialHtml)
  }, [initialHtml])

  return (
    <Tooltip content={disabledReason}>
      <ContentEditable
        innerRef={contentEditableRef}
        html={html}
        disabled={disabled}
        onChange={handleChange}
        tagName={tagName}
        className={cn('outline-transparent outline-0', disabled && 'cursor-default', className)}
        onKeyDown={handleKeyDown}
        data-testid={dataTestId}
      />
    </Tooltip>
  )
}

interface InlineSelectProps {
  className?: string
  options: SelectOption[]
  value: string
  onChange: (value: string | string[]) => void
  searchable?: boolean
  disabled?: boolean
  disabledReason?: string
}

export const InlineSelect = ({
  className = '',
  options,
  value,
  onChange,
  searchable = true,
  disabled = false,
  disabledReason,
}: InlineSelectProps) => (
  <Tooltip content={disabledReason}>
    <div className={cn('flex', disabled && 'cursor-not-allowed')}>
      <Select
        inline
        className={cn(
          className,
          'min-h-8 px-2 rounded',
          '[&>button]:p-0 [&>button]:justify-start [&>button]:min-h-8',
          !disabled && '[&>button]:bg-transparent hover:bg-bg2 [&>button]:text-inherit',
        )}
        variant="ghost"
        options={options}
        value={value}
        onChange={onChange}
        searchable={searchable}
        disabled={disabled}
      />
    </div>
  </Tooltip>
)

interface InlineDateTimePickerProps {
  onChange: (date: Date | undefined) => void
  defaultValue?: Date
  className?: string
  timeFormat?: TimePickerFormat
  disabled?: boolean
  disabledReason?: string
  datesExcluded?: Matcher
}

export const InlineDateTimePicker = ({
  onChange,
  defaultValue,
  timeFormat = '12hours',
  className = '',
  disabled = false,
  disabledReason,
  datesExcluded,
}: InlineDateTimePickerProps) => (
  <Tooltip content={disabledReason}>
    <div className={cn('flex', disabled && 'cursor-not-allowed')}>
      <DateTimePicker
        disabled={disabled}
        timeFormat={timeFormat}
        defaultValue={defaultValue}
        onChange={onChange}
        className={cn(
          'border-none shadow-none px-2 py-0 hover:bg-bg2',
          disabled && 'disabled:opacity-100',
          className,
        )}
        datesExcluded={datesExcluded}
      />
    </div>
  </Tooltip>
)

export default InlineEdit
